import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Emitter } from "pixi-particles";
import { Signal } from "type-signals";
import { Ease, Tween } from "@westpierstudio/luma/createjs";
import { Vec3, Model, Container3D, Camera, LightingEnvironment, Mesh3D, InstancedModel, Animation, StandardMaterial } from "pixi3d";
import { toMaterials, getModelChild, setModelAnimFrame, playModelAnim} from "./3DUtils";
import Ammo from "ammojs-typed";
import { asTriMesh, quatArr, setupCollider, vec3Arr } from "./PhysicsUtils";
import { setupLightsAndCamera } from "./SetupScene";
import { cloneDeep, sample } from "lodash";
import { DEG_TO_RAD, Container, Ellipse } from "pixi.js";
import { seedCoinData } from "./SeedPhysicsData";
import { ElasticCache, ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { minimumHeightSpecSelector, orientationSpecSelector } from "utils/Responsive";
import { devLog } from "utils/Misc";
import { factory } from "@westpierstudio/luma";

type CoinScaleStop = {
    scale: number, stop: number, ease?: (amount: number) => number
}

type CoinScaleStops = CoinScaleStop[];

const COIN_FLY_TIME = 1200;
const COIN_FLY_ROTATION = 360;
const COIN_SCALE_STOPS: CoinScaleStops = [
    {scale: 0.75, stop: 0},
    {scale: 4, stop: 0.25, ease: Ease.sineInOut},
    {scale: 5, stop: 0.4, ease: Ease.sineInOut},
    {scale: 4, stop: 0.65, ease: Ease.sineInOut},
    {scale: 1, stop: 1, ease: Ease.sineOut}
];

const MAX_COINS = 100;
const COIN_PHYSICS_SETTLE_TIME = 8000;
const COIN_PHYSICS_SPAWN_RANDOM_VELOCITY_RANGE = 0.15;
const COIN_PHYSICS_SPAWN_VERTICAL_VELOCITY = -0.01;
const COIN_PHYSICS_SPAWN_MOMENTUM = 0.5;
const COIN_SPAWN_LEVEL_2_THRESHOLD = 75;
const COIN_SPAWN_LEVEL_1_THRESHOLD = 20;
const JACKPOT_SEQUENCE_COINS_REMAINING = 10;
const GLOW_FADE_IN_OFFSET = 1000;
const GLOW_FADE_IN_TIME = 1000;
const GLOW_FADE_OUT_TIME = 1000;

let COIN_REACTION_ANIMATION_ENABLED = true;

const COIN_PHYSICS_PROPERTIES = {
    restitution: 0,
    damping: {linear: 0.25, angular: 0.1},
    friction: 0.8,
};

class JackpotBowlSignals {
    startCoinPour = new Signal();
    countUpComplete = new Signal();
    coinMoveComplete = new Signal();
    coinCollision = new Signal<(coin: number)=>void>();
    coinBowlTouch = new Signal();
    winComplete = new Signal();
}

type RigidBodyCustomData = {
    collided: boolean
}

export class JackpotBowl extends View {
    private readonly coins: Emitter;
    signals = new JackpotBowlSignals();

    protected sceneContainer: Container3D;
    protected coinModel: Model;
    protected physicsSpawnContainers: Mesh3D[][] = [];
    protected pot: Model & Container;
    protected potGlow: Container3D;
    protected potInnerContainer: Container3D;
    protected jackpotCoinsContainer: Container3D;
    protected currentPotAnimation?: Animation;
    protected activeCoins: {model: InstancedModel, body: Ammo.btRigidBody, settled: boolean}[] = [];
    protected physicsWorld: Ammo.btDiscreteDynamicsWorld;
    protected coinShape: Ammo.btCylinderShape;
    protected inWinAnimation: boolean = false;

    protected trailEmitterYellowCache: ElasticCache<Emitter>;
    protected trailEmitterWhiteCache: ElasticCache<Emitter>;

    protected coinsInFlight = 0;
    protected coinsInFlightModels: InstancedModel[] = [];
    protected activeEmitterItemPairs: {yellow: ExternalItem<Emitter>, white: ExternalItem<Emitter>}[] = [];
    protected debugCoinCountUpdateCallback?: (count: number) => void;

    constructor(protected override services: GameServices) {
        super(services);

        const {factory, resources, layout, application} = services;

        const spec = resources.getSpec(`jackpotbowl`);

        factory.applySpec(this, spec);

        this.trailEmitterYellowCache = new ElasticCache(
            () => factory.createParticleEmitter(resources.getSpec(`particles`).cointrailyellow),
            (emitter) => {
                this.addChild(emitter.parent);
            },
            (emitter)=>{
                this.removeChild(emitter.parent);
            }
        );

        this.trailEmitterWhiteCache = new ElasticCache(
            () => factory.createParticleEmitter(resources.getSpec(`particles`).cointrailwhite),
            (emitter) => {
                this.addChild(emitter.parent);
            },
            (emitter)=>{
                this.removeChild(emitter.parent);
            }
        );

        const camera = Camera.main;
        const lightingEnvironment = LightingEnvironment.main;

        (<any>window).camera = camera;
        (<any>window).lightingEnvironment = lightingEnvironment;

        setupLightsAndCamera(services, spec);

        this.sceneContainer = new Container3D();
        this.sceneContainer.name = `ScenePosition`;
        this.addChild(this.sceneContainer);

        this.pot = factory.createModel(<any>{
            ...spec.pot,
            position: {x:0, y: -0.1404515, z:1.536389},
            rotation: {x:-105, y:0, z:0},
            scale: 1,
        }, this.sceneContainer);

        this.coinModel = this.services.factory.createModel(spec.coin, this.pot);
        this.coinModel.z = -Infinity; // put it away behind the camera, it's just used as a template for spawning other instanced coins

        this.potInnerContainer = getModelChild(this.pot, `Pot`);
        const potPhysMeshContainer = getModelChild(this.pot, `PhysicsMeshContainer`);
        const potPhysMesh = <Mesh3D>getModelChild(potPhysMeshContainer, `PhysicsMesh`);
        this.potGlow = getModelChild(this.pot, `Plane`);
        this.potGlow.visible = false;
        const potGlowMesh = <Mesh3D>getModelChild(this.potGlow, `Plane`);
        potPhysMeshContainer.visible = false;

        this.jackpotCoinsContainer = new Container3D();
        this.jackpotCoinsContainer.name = `jackpotCoinsContainer`;
        this.potInnerContainer.addChild(this.jackpotCoinsContainer);


        for (let level = 0; level < 3; level++) {
            this.physicsSpawnContainers[level] = [];
            const spawns = level === 2 ? 1 : 5;
            for (let spawn = 0; spawn < spawns; spawn++) {
                const spawnContainer = <Mesh3D>getModelChild(this.pot, `Spawn_${level}_${spawn}`);
                this.physicsSpawnContainers[level].push(spawnContainer);
            }
        }

        (potGlowMesh.material as StandardMaterial).state.depthTest = false;
        (potGlowMesh.material as StandardMaterial).unlit = true;
        this.potGlow.alpha = 0;

        toMaterials(this.pot).forEach(material => {
            material.exposure = 1.3;
            material.metallic = 0.45;
            material.roughness = 0.45;
        });

        toMaterials(this.coinModel).forEach(material => {
            material.exposure = 1.6;
            material.metallic = 0.45;
            material.roughness = 0.95;
        });

        // set up the world
        const collisionConfiguration = new Ammo.btDefaultCollisionConfiguration();
        const dispatcher = new Ammo.btCollisionDispatcher(collisionConfiguration);
        const overlappingPairCache = new Ammo.btDbvtBroadphase();
        const solver = new Ammo.btSequentialImpulseConstraintSolver();
        this.physicsWorld  = new Ammo.btDiscreteDynamicsWorld(dispatcher, overlappingPairCache, solver, collisionConfiguration);

        this.physicsWorld.setGravity(new Ammo.btVector3(0, 0, -1));

        //mesh coin
        const safety = 0.0007; // increase the coin hitbox thickness
        const hickness = 0.004 + safety;
        const radius = 0.027 + safety;
        this.coinShape = new Ammo.btCylinderShape(new Ammo.btVector3(radius, hickness, hickness));
        this.coinShape.setMargin(0.0001);

        //mesh pot
        const potMeshShape = new Ammo.btBvhTriangleMeshShape(asTriMesh(potPhysMesh), false);
        potMeshShape.setMargin(0.0001);
        const potMeshBody = setupCollider(potMeshShape, 0, potPhysMeshContainer.position, potPhysMesh.rotationQuaternion);
        potMeshBody.setRestitution(0);
        this.physicsWorld.addRigidBody(potMeshBody);

        application.onTick.add((dt) => {
            this.physicsWorld.stepSimulation(dt, 4, 1/60);

            this.activeCoins.forEach(coinData => {
                if (!coinData.settled) {
                    coinData.model.position.setFrom(vec3Arr(coinData.body.getWorldTransform().getOrigin()));
                    coinData.model.rotationQuaternion.setFrom(quatArr(coinData.body.getWorldTransform().getRotation()));
                }

                // safety glitch prevention
                if (coinData.model.z <= 0) {
                    coinData.settled = true;
                    coinData.model.visible = false;
                }
            });

            this.detectCollision();
        });

        this.coins = factory.createParticleEmitter(resources.getSpec(`particles`).jackpotwincoins, this);

        services.layout.onResize.add(this.resize, this);
        this.on(`added`, this.resize, this);
        this.resize();

        setModelAnimFrame(this.pot, `default-hold`, 0);
        this.deserialiseActiveCoins();

        // setup jackpot bowl interaction behaviour
        const {x, y, halfWidth, halfHeight} = this.services.resources.getSpec(`jackpotbowl`).hitArea;
        this.hitArea = new Ellipse(x, y, halfWidth, halfHeight);
        this.on(`click`, () => this.playerInteract());
        this.on(`tap`, () => this.playerInteract());
        this.setInteractive(true);
    }

    getDebugMaterials() {
        return {
            bowl: toMaterials(this.pot)[0],
            coin: toMaterials(this.coinModel)[0]
        };
    }

    getDebugPhsyics() {
        return {
            world: this.physicsWorld,
            coinProperties: COIN_PHYSICS_PROPERTIES
        };
    }

    setDebugCoinCountUpdater(update: (count: number) => void) {
        this.debugCoinCountUpdateCallback = update;
    }

    setDebugCoinReactionEnabled(enabled: boolean) {
        COIN_REACTION_ANIMATION_ENABLED = enabled;
    }

    getDebugCoinReactionEnabled() {
        return COIN_REACTION_ANIMATION_ENABLED;
    }

    getDebugCoinCount() {
        return this.activeCoins.length + this.coinsInFlight;
    }

    setDebugGravity(strength: number) {
        this.physicsWorld.setGravity(new Ammo.btVector3(0, 0, strength));
    }

    setDebugCoinPhysicsProperties(coinProperties: typeof COIN_PHYSICS_PROPERTIES) {
        for (const {body} of this.activeCoins) {
            body.setRestitution(coinProperties.restitution);
            body.setDamping(coinProperties.damping.linear, coinProperties.damping.angular);
            body.setFriction(coinProperties.friction);
        }
    }

    setCoinTint(r: number, g: number, b: number, a: number) {
        for (const {model} of this.activeCoins) {
            toMaterials(model as unknown as Model).forEach(material => {
                material.baseColor.r = r;
                material.baseColor.g = g;
                material.baseColor.b = b;
                material.baseColor.a = a;
            });
        }
    }

    resize() {
        const bowlSpec = this.services.resources.getSpec(`jackpotbowl`);
        const portraitSpec = minimumHeightSpecSelector(this.services, bowlSpec.portrait);
        const {potRender} = bowlSpec;

        this.y = portraitSpec.y || 0;

        const actualBucket = this.services.layout.calculateLayout();

        const defaultSize =  (1 / potRender.size); // set general scale factor to get pot the correct size on screen
        const screenSize = innerHeight / 2; // adjust based on the screen size as orthographicSize is defined as 'The visible area from center of the screen to the top.'
        const stageSize = (1 / actualBucket.stageScale); // adjust inversely proportional to the stage scale, so that when the stage is scaled from horizontal squishing, 3D scales with the stage
        Camera.main.orthographicSize = defaultSize * stageSize * screenSize;

        // adjust the scene position in 3D to keep the pot where it is supposed to be in the 2D scene.
        const positionLocal = potRender;
        const positionGlobal = this.toGlobal(positionLocal);
        const position3D = Camera.main.screenToWorld(positionGlobal.x, positionGlobal.y, 0);
        if (!position3D) {
            throw new Error(`Camera position was undefined`);
        }
        this.sceneContainer.position.setFrom(position3D.array);
    }

    spawnNewCoin(screenSpaceSpawnPosition: {x: number, y: number}) {

        this.coinsInFlight++;
        this.maintainMaximumCoinsCap();

        if (this.debugCoinCountUpdateCallback) {
            this.debugCoinCountUpdateCallback(this.coinsInFlight + this.activeCoins.length);
        }

        const spawnDepth = 1;
        const coinPosition = Camera.main.screenToWorld(screenSpaceSpawnPosition.x, screenSpaceSpawnPosition.y, spawnDepth);

        if (!coinPosition) {
            throw new Error(`coinPosition was undefined`);
        }

        // transform the worldSpace coin spawn position into the scene container.
        const worldSpaceCoinPos = Vec3.fromValues(coinPosition.x, coinPosition.y, coinPosition.z);
        const sceneSpaceCoinPos = Vec3.transformMat4(
            worldSpaceCoinPos,
            this.sceneContainer.transform.inverseWorldTransform.array
        );

        // spawn in a new coin model instance
        const coinInstance = this.coinModel.createInstance();
        coinInstance.position.setFrom(sceneSpaceCoinPos);
        coinInstance.rotationQuaternion.setEulerAngles(90, 0, 0);
        this.sceneContainer.addChild(coinInstance);
        coinInstance.updateTransform();

        this.coinsInFlightModels.push(coinInstance);

        const physicsSpawnLocation = this.chooseSpawnContainer();
        this.animateCoinFlightFromReelToPot(screenSpaceSpawnPosition, coinInstance, physicsSpawnLocation);
    }

    maintainMaximumCoinsCap() {
        // 2 steps to ensure the best look, while having a hard cap.
        // pretty-sweep: remove coins who have settled, newest-oldest
        const physicsCoins = this.activeCoins.length;
        const coinsInFlight = this.coinsInFlight;
        const coinsToRemove = Math.max(0, (physicsCoins + coinsInFlight) - MAX_COINS);
        let removed = 0;
        for (let i = this.activeCoins.length - 1; i >= 0 && coinsToRemove > removed; i--) {
            if (this.activeCoins[i].settled) {
                const {body, model} = this.activeCoins.splice(i, 1)[0];
                this.physicsWorld.removeRigidBody(body);
                model.visible = false; // for some reason removing from parent does not stop rendering in pixi3d?
                model.parent.removeChild(model);
                removed++;
            }
        }
        // aggressive-sweep: remove any coins even if not settled, newest to oldest
        for (let i = this.activeCoins.length - 1; i >= 0 && coinsToRemove > removed; i--) {
            const {body, model} = this.activeCoins.splice(i, 1)[0];
            this.physicsWorld.removeRigidBody(body);
            model.visible = false; // for some reason removing from parent does not stop rendering in pixi3d?
            model.parent.removeChild(model);
            removed++;
        }


        if (this.debugCoinCountUpdateCallback) {
            this.debugCoinCountUpdateCallback(this.coinsInFlight + this.activeCoins.length);
        }
    }

    chooseSpawnContainer() {
        const currentCoinCount = this.activeCoins.length;

        let level: number;

        if (currentCoinCount > COIN_SPAWN_LEVEL_2_THRESHOLD) {
            level = 2;
        } else if (currentCoinCount > COIN_SPAWN_LEVEL_1_THRESHOLD) {
            level = 1;
        } else {
            level = 0;
        }

        if (level === 2) {
            return this.physicsSpawnContainers[level][0]; // there is only one container at level 2
        }

        const weightedIndices = (level === 1)
            ? [0,1,1,1,2,2,2,3,3,3,4]
            : [0,1,1,2,2,3,3,4];

        const spawnIndex = sample(weightedIndices) as number;

        return this.physicsSpawnContainers[level][spawnIndex];
    }

    animateCoinFlightFromReelToPot(screenSpaceSpawnPosition: {x: number, y: number}, coinInstance: InstancedModel, physicsSpawnContainer: Mesh3D) {

        const trailEmitterItems = this.spawnTrailEmitters(screenSpaceSpawnPosition);

        this.activeEmitterItemPairs.push(trailEmitterItems);

        const maxParticleLifeMilliseconds = Math.max(
            trailEmitterItems.yellow.reference.maxLifetime,
            trailEmitterItems.white.reference.maxLifetime
        ) * 1000;

        const flyingCoinCurrentPosition = {
            x: coinInstance.worldTransform.position.x,
            z: coinInstance.worldTransform.position.z,
        };

        const flyingCoinCurrentHeight = {
            y: coinInstance.worldTransform.position.y,
        };

        const flyingCoinTargetPosition = {
            x: physicsSpawnContainer.worldTransform.position.x,
        };

        const flyingCoinTargetHeight = {
            y: physicsSpawnContainer.worldTransform.position.y
        };

        const heightTween = Tween.get(flyingCoinCurrentHeight)
            .to(flyingCoinTargetHeight, COIN_FLY_TIME, Ease.sineOut);

        Tween.get(flyingCoinCurrentPosition)
            .to(flyingCoinTargetPosition, COIN_FLY_TIME);

        heightTween.on(`change`, () => {
            // transform the worldSpace coin position into the scene container.
            const worldSpaceCoinPos = Vec3.fromValues(flyingCoinCurrentPosition.x, flyingCoinCurrentHeight.y, flyingCoinCurrentPosition.z);
            Vec3.transformMat4(
                worldSpaceCoinPos,
                this.sceneContainer.transform.inverseWorldTransform.array,
                coinInstance.position.array
            );

            const screenSpaceCoinPos = Camera.main.worldToScreen(worldSpaceCoinPos[0], worldSpaceCoinPos[1], worldSpaceCoinPos[2]);
            const componentSpaceCoinPos = this.toLocal(screenSpaceCoinPos);
            trailEmitterItems.yellow.reference.updateSpawnPos(componentSpaceCoinPos.x, componentSpaceCoinPos.y);
            trailEmitterItems.white.reference.updateSpawnPos(componentSpaceCoinPos.x, componentSpaceCoinPos.y);
        });


        const scaleTween = Tween.get(coinInstance.scale);
        COIN_SCALE_STOPS.forEach(({stop, scale, ease}, index) => {
            if (stop === 0) {
                coinInstance.scale.set(scale);
                return;
            }
            const previousStop = COIN_SCALE_STOPS[index - 1].stop;
            scaleTween.to({x: scale, y: scale, z: scale}, (stop - previousStop) * COIN_FLY_TIME, ease);
        });

        const rotation = {angle: 0};
        const finalRotation = {angle: COIN_FLY_ROTATION};
        Tween.get(rotation)
            .to(finalRotation, COIN_FLY_TIME)
            .on(`change`, () => {
                coinInstance.rotationQuaternion.setEulerAngles(90, rotation.angle, 0);
            });

        const coinFlyTime_s = COIN_FLY_TIME / 1000;
        const rotationMatchAngularVelocity = (finalRotation.angle / coinFlyTime_s) * DEG_TO_RAD * 0.75;

        const randomVelocityX = Math.random() * COIN_PHYSICS_SPAWN_RANDOM_VELOCITY_RANGE - (COIN_PHYSICS_SPAWN_RANDOM_VELOCITY_RANGE / 2);
        const randomVelocityY = (Math.random() * COIN_PHYSICS_SPAWN_RANDOM_VELOCITY_RANGE - (COIN_PHYSICS_SPAWN_RANDOM_VELOCITY_RANGE / 2) ) * 4;

        const horizontalVelocity = (flyingCoinTargetPosition.x - flyingCoinCurrentPosition.x) / coinFlyTime_s * COIN_PHYSICS_SPAWN_MOMENTUM;


        const velocity = new Ammo.btVector3(horizontalVelocity + randomVelocityX, randomVelocityY, COIN_PHYSICS_SPAWN_VERTICAL_VELOCITY);
        const angularVelocity = new Ammo.btVector3(randomVelocityX, 0, rotationMatchAngularVelocity);

        Tween.get({})
            .wait(COIN_FLY_TIME)
            .call(() => this.spawnPhysicsCoin(coinInstance, physicsSpawnContainer, velocity, angularVelocity))
            .wait(maxParticleLifeMilliseconds)
            .call(() => {
                this.activeEmitterItemPairs.shift();
                this.trailEmitterYellowCache.free(trailEmitterItems.yellow);
                this.trailEmitterWhiteCache.free(trailEmitterItems.white);
            });
    }

    spawnTrailEmitters(screenSpaceSpawnPosition: {x:number, y:number}) {
        const trailEmitterItems = {
            yellow: this.trailEmitterYellowCache.get(),
            white: this.trailEmitterWhiteCache.get()
        };

        const componentSpaceStartPosition = this.toLocal(screenSpaceSpawnPosition);

        trailEmitterItems.yellow.reference.updateSpawnPos(componentSpaceStartPosition.x, componentSpaceStartPosition.y);
        trailEmitterItems.yellow.reference.resetPositionTracking();
        trailEmitterItems.yellow.reference.emit = true;

        trailEmitterItems.white.reference.updateSpawnPos(componentSpaceStartPosition.x, componentSpaceStartPosition.y);
        trailEmitterItems.white.reference.resetPositionTracking();
        trailEmitterItems.white.reference.emit = true;

        return trailEmitterItems;
    }

    spawnPhysicsCoin(coinInstance: InstancedModel, spawnContainer: Container3D, velocity: Ammo.btVector3, angularVelocity: Ammo.btVector3) {

        this.signals.coinMoveComplete.dispatch();

        this.coinsInFlight--;
        this.coinsInFlightModels.shift();

        this.potInnerContainer.addChild(coinInstance);
        coinInstance.rotationQuaternion.setEulerAngles(0, 0, 0);

        const coinBody = setupCollider(this.coinShape, 20, spawnContainer.position, coinInstance.rotationQuaternion);
        coinBody.setRestitution(COIN_PHYSICS_PROPERTIES.restitution);
        coinBody.setDamping(COIN_PHYSICS_PROPERTIES.damping.linear, COIN_PHYSICS_PROPERTIES.damping.angular);
        coinBody.setFriction(COIN_PHYSICS_PROPERTIES.friction);
        coinBody.setAngularVelocity(angularVelocity);
        coinBody.setLinearVelocity(velocity);
        (<any>coinBody).userData = {collided: false} as RigidBodyCustomData;

        const activeCoinData = {model: coinInstance, body: coinBody, settled: false};
        this.activeCoins.push(activeCoinData);
        this.physicsWorld.addRigidBody(coinBody);

        // prevent the coin from moving again by setting it's mass to 0, making it an immovable collider
        Tween.get({})
            .wait(COIN_PHYSICS_SETTLE_TIME)
            .call(() => {
                activeCoinData.settled = true;
                coinBody.setMassProps(0, new Ammo.btVector3(0, 0, 0));
            });
    }


    serialiseActiveCoins() {
        const data = this.activeCoins.map(({body}) => {
            return {
                position: vec3Arr(body.getWorldTransform().getOrigin()),
                rotation: quatArr(body.getWorldTransform().getRotation())
            };
        });
        console.log(`export const seedCoinData = ${JSON.stringify(data)};`);
    }

    deserialiseActiveCoins() {
        seedCoinData.forEach(coinData => {
            const coinInstance = this.coinModel.createInstance();
            coinInstance.position.setFrom(coinData.position);
            coinInstance.rotationQuaternion.setFrom(coinData.rotation);
            this.potInnerContainer.addChild(coinInstance);
            const coinBody = setupCollider(this.coinShape, 20, coinInstance.position, coinInstance.rotationQuaternion);
            coinBody.setMassProps(0, new Ammo.btVector3(0, 0, 0));
            this.physicsWorld.addRigidBody(coinBody);
            this.activeCoins.push({model: coinInstance, body: coinBody, settled: true});
        });
    }

    detectCollision(){

        const dispatcher = this.physicsWorld.getDispatcher();
        const numManifolds = dispatcher.getNumManifolds();

        for ( let i = 0; i < numManifolds; i ++ ) {

            const contactManifold = dispatcher.getManifoldByIndexInternal( i );
            const numContacts = contactManifold.getNumContacts();

            for ( let j = 0; j < numContacts; j++ ) {

                const contactPoint = contactManifold.getContactPoint( j );
                const distance = contactPoint.getDistance();

                if( distance > 0.0 ) continue;

                // collision could occur between the coin+pot, or coin+coin.
                // the pot has no user data, but if something WITH userData that hasn't collided yet, it must be a coin, so make a noise.

                // @ts-ignore for some reason castObject is missing from the type... :(
                const body0 = Ammo.castObject( contactManifold.getBody0(), Ammo.btRigidBody );
                // @ts-ignore for some reason castObject is missing from the type... :(
                const body1 = Ammo.castObject( contactManifold.getBody1(), Ammo.btRigidBody );

                const data0 = (<any>body0).userData as RigidBodyCustomData;
                const data1 = (<any>body1).userData as RigidBodyCustomData;

                const collided0 = data0 ? data0.collided : true;
                const collided1 = data1 ? data1.collided : true;

                if (!collided0 || !collided1) {
                    if (data0) {
                        data0.collided = true;
                    }
                    if (data1) {
                        data1.collided = true;
                    }
                    this.signals.coinCollision.dispatch(this.activeCoins.length);
                    if (COIN_REACTION_ANIMATION_ENABLED) {
                        if (!this.currentPotAnimation) {
                            this.currentPotAnimation = playModelAnim(this.pot, `coin-reaction`).once(`complete`, () => {
                                this.currentPotAnimation = undefined;
                            });
                        }
                    }
                }
            }
        }

    }

    startWinLoop(): void {

        const settings = this.services.resources.getSetting(`jackpotBowl`);
        this.inWinAnimation = true;

        this.currentPotAnimation = playModelAnim(this.pot, `jackpot-IN`).once(`complete`, () => {
            this.signals.startCoinPour.dispatch();
            this.coins.playOnce();
            this.currentPotAnimation = undefined;

            this.jackpotSequenceRemoveCoins();

            Tween.get(this)
                .wait(settings.countUpTime)
                .call(() => {
                    this.signals.countUpComplete.dispatch();
                });
        });

        Tween.get(this.potGlow)
            .wait(GLOW_FADE_IN_OFFSET)
            .to({alpha: 1}, GLOW_FADE_IN_TIME);
    }

    jackpotSequenceRemoveCoins(remainingCoins = JACKPOT_SEQUENCE_COINS_REMAINING) {
        const physicsCoins = this.activeCoins.length;
        const coinsInFlight = this.coinsInFlight;
        const coinsToRemove = Math.max(0, (physicsCoins + coinsInFlight) - remainingCoins);

        this.activeCoins.sort((a,b) => b.model.z - a.model.z);
        const removed = this.activeCoins.splice(0, coinsToRemove);
        for (const {body, model} of removed) {
            this.physicsWorld.removeRigidBody(body);
            model.visible = false; // for some reason removing from parent does not stop rendering in pixi3d?
            model.parent.removeChild(model);
        }


        if (this.debugCoinCountUpdateCallback) {
            this.debugCoinCountUpdateCallback(this.coinsInFlight + this.activeCoins.length);
        }
    }

    endWinLoop(): void {

        this.stopEmitters();

        // If the bowl is already animating (From startWinLoop), work out how far through and
        // and skip the return to that point, so it doesn't snap to the end and then animate back.
        let fraction = 1;
        if (this.currentPotAnimation) {
            this.currentPotAnimation.removeAllListeners();
            fraction = this.currentPotAnimation.position / this.currentPotAnimation.duration;
            this.currentPotAnimation.stop();
            this.jackpotSequenceRemoveCoins();
        }

        this.currentPotAnimation = playModelAnim(this.pot, `jackpot-OUT`).once(`complete`, () => {
            this.inWinAnimation = false;
            this.signals.winComplete.dispatch();
            this.currentPotAnimation = undefined;
        });
        this.currentPotAnimation.position = this.currentPotAnimation.duration * (1 - fraction);

        Tween.get(this.potGlow, {override: true})
            .to({alpha: 0}, GLOW_FADE_OUT_TIME)
            .setPosition(1 - fraction);
    }

    skipJackpot(): void {

        Tween.removeTweens(this);
        this.currentPotAnimation?.removeAllListeners();
        this.signals.countUpComplete.dispatch();
    }

    playerInteract(): void {

        if (!this.currentPotAnimation && !this.inWinAnimation) {

            this.currentPotAnimation = playModelAnim(this.pot, `coin-reaction`).once(`complete`, () => {
                this.currentPotAnimation = undefined;
            });
            this.signals.coinBowlTouch.dispatch();
        }
    }

    setInteractive(interactive: boolean): void {

        this.interactive = this.buttonMode = interactive;
    }

    override reset(): void {
        this.stopEmitters();
        this.cleanupEmitters();
        setModelAnimFrame(this.pot, `default-hold`, 0);
        this.potGlow.alpha = 0;
        if (this.currentPotAnimation) {
            this.currentPotAnimation.removeAllListeners();
            this.currentPotAnimation.stop();
            this.currentPotAnimation = undefined;
        }
        if (this.coinsInFlight > 0) {
            for (const coinInstance of this.coinsInFlightModels) {
                coinInstance.visible = false;
                coinInstance.parent.removeChild(coinInstance);
            }
        }
        if (this.activeEmitterItemPairs.length > 0) {
            for (const {yellow, white} of this.activeEmitterItemPairs) {
                yellow.reference.emit = false;
                yellow.reference.cleanup();
                this.trailEmitterYellowCache.free(yellow);
                white.reference.emit = false;
                white.reference.cleanup();
                this.trailEmitterWhiteCache.free(white);
            }
        }
        this.activeEmitterItemPairs = [];
        this.coinsInFlightModels = [];
        this.jackpotSequenceRemoveCoins();
        this.deserialiseActiveCoins();

        this.spawnTrailEmitters;

        this.inWinAnimation = false;
    }

    private stopEmitters(): void {
        this.coins.emit = false;
    }

    private cleanupEmitters(): void {
        this.coins.cleanup();
    }

    get winAnimation(): boolean {
        return this.inWinAnimation;
    }
}